// Points tab — AG Grid Enterprise table of every SamplePoint for a campaign.
// Columns are generated from the campaign config; oracle-mode enables extra
// truth/delta columns. Row click opens a small read-only detail popover.
//
// AG Grid modules (community + enterprise) are registered globally in
// src/main.tsx, so no registration is needed here.

import { useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  colorSchemeDark,
  themeQuartz,
  type ColDef,
  type GridOptions,
  type ICellRendererParams,
  type RowClickedEvent,
  type ValueFormatterParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import { Badge, Box, Dialog, Flex, Heading, Text } from '@radix-ui/themes';

import type { CampaignState, SamplePoint } from '../types';
import { useCampaignStateQuery } from '../api';
import { useTheme } from '../../../context/ThemeContext';

// AG Grid v33+ Theming API — matches DataExplorer / ConfigurationPanel.
// Do NOT import ag-grid-community/styles/*.css here: the legacy stylesheets
// would leak into the global bundle and collide with the Theming-API
// output used by other grids in Thread.

export type PointsTabProps = {
  campaignId: string;
};

const STATUS_COLORS: Record<
  SamplePoint['status'],
  'amber' | 'blue' | 'green' | 'red' | 'gray'
> = {
  queued: 'gray',
  running: 'blue',
  done: 'green',
  failed: 'red',
};

export function PointsTab({ campaignId }: PointsTabProps) {
  const stateQuery = useCampaignStateQuery(campaignId);
  const state = stateQuery.data;
  const { theme } = useTheme();

  const gridTheme = useMemo(
    () => (theme === 'dark' ? themeQuartz.withPart(colorSchemeDark) : themeQuartz),
    [theme],
  );

  const [selected, setSelected] = useState<SamplePoint | null>(null);

  const { columnDefs, rows } = useMemo(
    () => (state ? buildGrid(state) : { columnDefs: [], rows: [] }),
    [state],
  );

  const gridOptions = useMemo<GridOptions<SamplePoint>>(
    () => ({
      animateRows: true,
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowHeight: 32,
      headerHeight: 34,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      },
      sideBar: 'columns',
      onRowClicked: (e: RowClickedEvent<SamplePoint>) => {
        if (e.data) setSelected(e.data);
      },
    }),
    [],
  );

  if (!state) {
    return (
      <Box p="4">
        <Text size="2" color="gray">
          Loading campaign state...
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" style={{ height: '100%' }}>
      <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
        <AgGridReact<SamplePoint>
          theme={gridTheme}
          rowData={rows}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
        />
      </Box>

      <PointDetailPopover
        point={selected}
        state={state}
        onClose={() => setSelected(null)}
      />
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Column + row builders
// ---------------------------------------------------------------------------

function buildGrid(state: CampaignState): {
  columnDefs: ColDef<SamplePoint>[];
  rows: SamplePoint[];
} {
  const inputs = state.config.inputs;
  const outputs = state.config.outputs;
  const oracleEnabled = state.config.oracle.enabled;
  const showTruth = oracleEnabled || state.config.sampler?.engine === 'tailor-mannequin';

  const cols: ColDef<SamplePoint>[] = [
    {
      headerName: 'iter',
      field: 'iter',
      width: 80,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'batch',
      field: 'batchId',
      width: 140,
    },
    {
      headerName: 'status',
      field: 'status',
      width: 110,
      cellRenderer: (
        p: ICellRendererParams<SamplePoint, SamplePoint['status']>,
      ) => (
        <Badge color={STATUS_COLORS[p.value ?? 'queued']} variant="soft" size="1">
          {p.value}
        </Badge>
      ),
    },
    {
      headerName: 'source',
      field: 'source',
      width: 100,
      cellRenderer: (
        p: ICellRendererParams<SamplePoint, SamplePoint['source']>,
      ) => {
        if (p.value === 'manual') {
          return (
            <Badge color="iris" variant="soft" size="1">
              M manual
            </Badge>
          );
        }
        return <span style={{ color: 'var(--gray-11)' }}>sampler</span>;
      },
    },
  ];

  cols.push({
    headerName: 'picked by',
    colId: 'picked_by',
    valueGetter: (p: ValueGetterParams<SamplePoint>) =>
      p.data?.samplerDecision?.selectedBy ?? null,
    cellRenderer: (p: ICellRendererParams<SamplePoint, string | null>) => {
      const value = p.value;
      if (!value) return <span style={{ color: 'var(--gray-9)' }}>—</span>;
      return (
        <Badge color="green" variant="soft" size="1">
          {value.replace('_', ' ')}
        </Badge>
      );
    },
    width: 150,
  });

  // Input columns (one per range axis).
  for (const axis of inputs) {
    if (axis.mode !== 'range') continue;
    cols.push({
      headerName: `${axis.name} (${axis.units})`,
      colId: `in_${axis.name}`,
      valueGetter: (p: ValueGetterParams<SamplePoint>) =>
        p.data?.inputs?.[axis.name] ?? null,
      valueFormatter: numericFormatter(4),
      width: 120,
      filter: 'agNumberColumnFilter',
    });
  }

  // Output columns + predicted (mean, sigma). Oracle-mode adds truth+delta.
  for (const o of outputs) {
    cols.push({
      headerName: o.name,
      colId: `out_${o.name}`,
      valueGetter: (p: ValueGetterParams<SamplePoint>) =>
        p.data?.outputs?.[o.name] ?? null,
      valueFormatter: numericFormatter(5),
      width: 110,
      filter: 'agNumberColumnFilter',
    });
    cols.push({
      headerName: `${o.name} predicted`,
      colId: `pred_${o.name}`,
      valueGetter: (p: ValueGetterParams<SamplePoint>) => {
        const pr = p.data?.predicted?.[o.name];
        return pr ? pr.mean : null;
      },
      valueFormatter: (p: ValueFormatterParams<SamplePoint, number | null>) => {
        const pr = p.data?.predicted?.[o.name];
        if (!pr) return '—';
        return `${pr.mean.toFixed(4)} (sigma ${pr.sigma.toFixed(4)})`;
      },
      width: 180,
    });

    if (showTruth) {
      cols.push({
        headerName: `${o.name} truth`,
        colId: `truth_${o.name}`,
        valueGetter: (p: ValueGetterParams<SamplePoint>) =>
          p.data?.truthOutputs?.[o.name] ?? null,
        valueFormatter: numericFormatter(5),
        width: 120,
      });
      cols.push({
        headerName: `${o.name} delta`,
        colId: `delta_${o.name}`,
        valueGetter: (p: ValueGetterParams<SamplePoint>) => {
          const truth = p.data?.truthOutputs?.[o.name];
          const pred = p.data?.predicted?.[o.name]?.mean;
          if (truth === undefined || pred === undefined) return null;
          return truth - pred;
        },
        valueFormatter: numericFormatter(5),
        width: 110,
      });
    }
  }

  // Tail columns.
  cols.push({
    headerName: 'runtime (s)',
    colId: 'runtime_s',
    valueGetter: (p: ValueGetterParams<SamplePoint>) => p.data?.runtimeSec ?? null,
    valueFormatter: numericFormatter(0),
    width: 110,
    filter: 'agNumberColumnFilter',
  });
  cols.push({
    headerName: 'cost (FC)',
    colId: 'cost_fc',
    valueGetter: (p: ValueGetterParams<SamplePoint>) =>
      p.data?.costFlexCredits ?? null,
    valueFormatter: numericFormatter(2),
    width: 100,
    filter: 'agNumberColumnFilter',
  });
  cols.push({
    headerName: 'Flow360 case',
    colId: 'flow360_case',
    valueGetter: (p: ValueGetterParams<SamplePoint>) =>
      p.data?.flow360CaseUrl ?? null,
    cellRenderer: (p: ICellRendererParams<SamplePoint, string | null>) => {
      const url = p.value;
      if (!url) return <span style={{ color: 'var(--gray-9)' }}>—</span>;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--accent-11)' }}
          onClick={(e) => e.stopPropagation()}
        >
          open
        </a>
      );
    },
    width: 120,
  });

  return { columnDefs: cols, rows: state.points };
}

function numericFormatter(digits: number) {
  return (p: ValueFormatterParams<SamplePoint, number | null>): string => {
    const v = p.value;
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return v.toFixed(digits);
  };
}

// ---------------------------------------------------------------------------
// Inline popover — surfaces/ManualSamplePopover is aimed at writing new
// manual samples on the heatmap; this Points-tab popover is a lightweight
// read-only detail view to avoid coupling to a different set of props.
// ---------------------------------------------------------------------------

function PointDetailPopover({
  point,
  state,
  onClose,
}: {
  point: SamplePoint | null;
  state: CampaignState;
  onClose: () => void;
}) {
  const open = point !== null;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content maxWidth="520px">
        {point && (
          <Flex direction="column" gap="3">
            <Heading size="3">
              Point {point.id}
              <Text size="2" color="gray" style={{ marginLeft: 8 }}>
                iter {point.iter} · batch {point.batchId}
              </Text>
            </Heading>

            <Flex gap="2" align="center">
              <Badge color={STATUS_COLORS[point.status]} variant="soft">
                {point.status}
              </Badge>
              <Badge
                color={point.source === 'manual' ? 'iris' : 'gray'}
                variant="soft"
              >
                {point.source}
              </Badge>
              {point.samplerDecision?.engine === 'tailor-mannequin' && (
                <Badge color="green" variant="soft">
                  {point.samplerDecision.selectedBy.replace('_', ' ')}
                </Badge>
              )}
            </Flex>

            {point.samplerDecision && (
              <Box>
                <Text size="1" color="gray">
                  Sampler decision
                </Text>
                <Box
                  style={{
                    background: 'var(--gray-a3)',
                    padding: 8,
                    borderRadius: 6,
                    fontFamily: 'var(--code-font-family, monospace)',
                    fontSize: 12,
                  }}
                >
                  <div>{point.samplerDecision.label}</div>
                  <div>{point.samplerDecision.reason}</div>
                  {point.samplerDecision.mannequinInstanceId && (
                    <div>
                      mannequin = {point.samplerDecision.mannequinInstanceId}
                    </div>
                  )}
                  {point.samplerDecision.frontierLimit != null && (
                    <div>
                      frontier alpha &lt;= {point.samplerDecision.frontierLimit.toFixed(3)}
                    </div>
                  )}
                  {point.samplerDecision.expertScores && (
                    <div>
                      {point.samplerDecision.expertScores
                        .map(
                          (s) =>
                            `${s.name}:${s.score.toFixed(2)}x${s.weight.toFixed(2)}`,
                        )
                        .join(' · ')}
                    </div>
                  )}
                </Box>
              </Box>
            )}

            <Box>
              <Text size="1" color="gray">
                Inputs
              </Text>
              <Box
                style={{
                  background: 'var(--gray-a3)',
                  padding: 8,
                  borderRadius: 6,
                  fontFamily: 'var(--code-font-family, monospace)',
                  fontSize: 12,
                }}
              >
                {state.config.inputs.map((axis) => {
                  const v = point.inputs[axis.name];
                  return (
                    <div key={axis.name}>
                      {axis.name} = {v !== undefined ? v : '—'} {axis.units}
                    </div>
                  );
                })}
              </Box>
            </Box>

            {point.outputs && (
              <Box>
                <Text size="1" color="gray">
                  Outputs
                </Text>
                <Box
                  style={{
                    background: 'var(--gray-a3)',
                    padding: 8,
                    borderRadius: 6,
                    fontFamily: 'var(--code-font-family, monospace)',
                    fontSize: 12,
                  }}
                >
                  {Object.entries(point.outputs).map(([k, v]) => {
                    const pr = point.predicted?.[k];
                    return (
                      <div key={k}>
                        {k} = {v}
                        {pr !== undefined &&
                          ` (pred ${pr.mean.toFixed(5)}, sigma ${pr.sigma.toFixed(5)})`}
                      </div>
                    );
                  })}
                </Box>
              </Box>
            )}

            {point.flow360CaseUrl && (
              <Text size="2">
                <a
                  href={point.flow360CaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent-11)' }}
                >
                  Open Flow360 case
                </a>
              </Text>
            )}

            <Flex justify="end">
              <Dialog.Close>
                <button
                  onClick={onClose}
                  style={{
                    background: 'var(--gray-a4)',
                    border: '1px solid var(--gray-a5)',
                    color: 'var(--gray-12)',
                    padding: '6px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </Dialog.Close>
            </Flex>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default PointsTab;
